/**
 * Catalog of named actions the Dash agent can reference and the runtime
 * can dispatch.
 *
 * Mirrors `vercel-labs/json-render`'s "two-sided catalog" pattern:
 * - **Declarations** (this file) — name + AI-visible description + a
 *   loose params shape. Fed into the system prompt so the AI knows which
 *   actions exist when it generates a Button or a `watch` binding.
 * - **Implementations** — registered at runtime by the host app via
 *   `actions-registry.tsx`. Different deployments can register different
 *   handlers without touching the catalog (e.g. customer-specific
 *   "send to Teams" wired up on top of the same `chat` action name).
 *
 * Companion to §1.4 (action-observer): every dispatched action flows
 * through `withActionObservers` so devtools and telemetry see it.
 */

import { z } from "zod"

/**
 * AI-visible declaration of an action. The `description` is included
 * verbatim in the system prompt; `paramsSchema` validates params at
 * dispatch time and is also serialised into the prompt so the AI knows
 * the shape it should pass.
 */
export interface ActionCatalogEntry {
  /** Stable action name (e.g. "setState", "drillTo", "exportCsv"). */
  name: string
  /** One-line description for the system prompt. */
  description: string
  /**
   * Zod schema for params. Use `z.object({})` for nullary actions so
   * the schema-to-prompt converter still emits "{}" rather than "any".
   */
  paramsSchema: z.ZodTypeAny
}

// ── Standard actions shipped with Dash ─────────────────────────────

/**
 * Programmatic state writes. The bread-and-butter action — used by
 * Buttons that toggle a filter, by `watch` bindings (§1.5) that need
 * to reset dependent state, by drill-down handlers, etc.
 */
export const setStateAction: ActionCatalogEntry = {
  name: "setState",
  description:
    "Write a value to the dashboard state at a JSON-Pointer path. Used by Buttons that toggle filters and by `watch` bindings to reset dependent fields when an upstream filter changes.",
  paramsSchema: z.object({
    path: z.string().describe("JSON-Pointer path into the dashboard state (e.g. /filters/region)"),
    value: z.unknown().describe("Any JSON-serialisable value, including null to clear"),
  }),
}

/**
 * Re-run a specific queryBinding on demand. The standard refresh path
 * already re-runs every binding when state changes; this action is for
 * Buttons that target a single tile (e.g. a "Reload" mini-button next
 * to a long-running chart).
 *
 * Implementation lives in the host app's registry — no built-in handler
 * here yet; the dispatcher returns "no handler" if nothing is wired.
 */
export const refreshBindingAction: ActionCatalogEntry = {
  name: "refreshBinding",
  description:
    "Re-run a specific queryBinding by id. Use when a single tile needs to refresh independently (e.g. a Reload button).",
  paramsSchema: z.object({
    binding_id: z.string().describe("The binding_id from queryBindings"),
  }),
}

/**
 * Push a parameterised sub-dashboard onto a navigation stack. The host
 * app decides whether "view" is a dashboard id, a route name, or a
 * pre-registered template — Dash just emits the action.
 */
export const drillToAction: ActionCatalogEntry = {
  name: "drillTo",
  description:
    "Push a parameterised sub-dashboard onto the navigation stack. Use for click-to-detail flows (e.g. clicking a customer row opens a per-customer dashboard).",
  paramsSchema: z.object({
    view: z.string().describe("Target view identifier"),
    params: z.record(z.string(), z.unknown()).optional(),
  }),
}

/**
 * Export a tile's data as CSV. The action expects either a binding_id
 * (snapshot the SQL output) or an inline data array (snapshot whatever
 * the tile is currently rendering).
 */
export const exportCsvAction: ActionCatalogEntry = {
  name: "exportCsv",
  description:
    "Export a tile's data as CSV. Provide binding_id for SQL-backed tiles or `data` for client-resolved arrays.",
  paramsSchema: z
    .object({
      binding_id: z.string().optional(),
      data: z.array(z.record(z.string(), z.unknown())).optional(),
      filename: z.string().optional(),
    })
    .refine((v) => v.binding_id !== undefined || v.data !== undefined, {
      message: "exportCsv requires either binding_id or data",
    }),
}

/**
 * Escape hatch back to the AI agent. The host wires it to whatever the
 * existing chat composer calls; the catalog declares it so the AI knows
 * "if nothing else fits, hand back to me with this prompt".
 */
export const chatAction: ActionCatalogEntry = {
  name: "chat",
  description:
    "Send a message to the Dash agent. Use sparingly — prefer setState / drillTo / refreshBinding for non-AI flows.",
  paramsSchema: z.object({
    prompt: z.string(),
  }),
}

/**
 * "Explain this tile" — opens an inline dialog with a 2-sentence
 * summary of what the user is currently looking at + an optional
 * anomaly callout. Cached by (tile_id, state_hash, data_hash) so
 * clicking Explain twice on an unchanged tile reuses the answer.
 *
 * Host-supplied handler is responsible for the actual agent call;
 * the catalog declaration makes it discoverable to the AI prompt
 * and to the right-click menu in the renderer.
 */
export const explainTileAction: ActionCatalogEntry = {
  name: "explainTile",
  description:
    "Open an inline explanation of a tile — the agent receives the tile's rendered data, binding SQL, and live filter state, then returns a 2-sentence summary plus an anomaly callout. Use when the user asks 'why' or 'what does this show' about a specific tile.",
  paramsSchema: z.object({
    tile_id: z.string().describe("Stable tile id from the spec or layout"),
  }),
}

/**
 * Default catalog shipped with Dash. The host registry provides
 * implementations for `setState` out of the box; the rest are stubs
 * until the host app wires them up.
 */
export const standardActions: ActionCatalogEntry[] = [
  setStateAction,
  refreshBindingAction,
  drillToAction,
  exportCsvAction,
  chatAction,
  explainTileAction,
]

/**
 * Build a Map of name -> entry for fast lookup at dispatch time.
 */
export function createActionCatalog(
  entries: ActionCatalogEntry[] = standardActions
): Map<string, ActionCatalogEntry> {
  const map = new Map<string, ActionCatalogEntry>()
  for (const entry of entries) {
    map.set(entry.name, entry)
  }
  return map
}
